/**
 * Rename a match: a new away and home team code, for a code typed wrong at
 * upload ("CROA - ESP @CROA" for ESP@CROA). Priori staff only. Each team
 * keeps its side: a home/away swap is refused (the server explains). The match
 * keeps its data, video, labels, ratings and notebooks; the hidden date on
 * the code stays (app/services/game_rename.py). Used on the Matches list
 * and the calibration page; clicks stay in the control, not a row link.
 */
import { useState } from 'react'
import type { KeyboardEvent, MouseEvent } from 'react'

import { ApiError, api } from '../lib/api'
import './games.css'

const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation()

/** The AWAY@HOME teams of a game code, without its date token — also a
 *  legacy one glued on (ATL@CLB05242026), as parse_game_code reads it. */
export function teamsOf(code: string): { away: string; home: string } {
  const [away = '', home = ''] = code.split('@')
  const glued = /\d{4,}\w*$/
  return { away: away.trim().replace(glued, ''),
           home: home.trim().replace(glued, '') }
}

export default function RenameMatch({ gameId, gameCode, onRenamed }: {
  gameId: number | null
  gameCode: string
  onRenamed: (code: string) => void
}) {
  const [editing, setEditing] = useState(false)
  const [away, setAway] = useState('')
  const [home, setHome] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  if (gameId == null) return null

  if (!editing) {
    return (
      <button type="button" className="gm-score-edit" aria-label="Rename match"
              onKeyDown={stop}
              onClick={(e) => {
                stop(e)
                const t = teamsOf(gameCode)
                setAway(t.away); setHome(t.home); setError(null)
                setEditing(true)
              }}>
        rename
      </button>
    )
  }

  const save = async () => {
    if (!away.trim() || !home.trim() || (away + home).includes('@')) {
      setError('Enter both team codes, without @.')
      return
    }
    setBusy(true); setError(null)
    try {
      const r = await api<{ game_code: string }>(
        `/api/v1/fixtures/games/${gameId}/rename`,
        { method: 'POST', body: JSON.stringify({ away, home }) })
      setEditing(false)
      onRenamed(r.game_code)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <span className="gm-score-cell" onClick={stop} onKeyDown={(e) => {
      stop(e)
      if (e.key === 'Enter') void save()
      if (e.key === 'Escape') { setEditing(false); setError(null) }
    }}>
      <input className="adm-input" style={{ width: '7em' }} aria-label="Away team code"
             value={away} autoFocus
             onChange={(e) => setAway(e.target.value.toUpperCase())} />
      <span aria-hidden>@</span>
      <input className="adm-input" style={{ width: '7em' }} aria-label="Home team code"
             value={home} onChange={(e) => setHome(e.target.value.toUpperCase())} />
      <button type="button" className="adm-btn" disabled={busy}
              onClick={() => void save()}>Save</button>
      <button type="button" className="adm-btn adm-btn--quiet"
              onClick={() => { setEditing(false); setError(null) }}>Cancel</button>
      <span className="gm-score-error">Each team keeps its side.</span>
      {error && <span className="gm-score-error" role="alert">{error}</span>}
    </span>
  )
}
